import { useEffect } from 'react';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { useDailySunlightStore } from '../../store/dailySunlightsStore';
import { buildDateSequence } from '../../utils/dateSequence';
import styles from './styles.module.scss';
import { Select } from '../UI/Select/Select';
import { Icon } from '../UI/Icon/Icon';
import { WeaveIconButton } from '../UI/WeaveIconButton/WeaveIconButton';

type AnimationType = 'daily' | 'facade';

interface AnimationControlsProps {
	animationType: AnimationType;
	// Daily sunlight animation props
	startDailyAnimation?: () => Promise<void>;
	stopDailyAnimation?: () => void;
	isAnimatingDaily?: boolean;
	calculateSingleDayDaily?: (dayIndex: number) => Promise<void>;
	clearDailyVisualization?: () => void;
	// Facade animation props
	startFacadeAnimation?: () => Promise<void>;
	stopFacadeAnimation?: () => void;
	isAnimatingFacade?: boolean;
	calculateSingleDayFacade?: (dayIndex: number) => Promise<void>;
	clearFacadeVisualization?: () => void;
	// Common props
	disabled?: boolean;
	calculatingInProgress?: boolean;
	facadeSunlightEnabled?: boolean;
}

export const AnimationControls = (props: AnimationControlsProps) => {
	const {
		animationType,
		startDailyAnimation,
		stopDailyAnimation,
		isAnimatingDaily = false,
		calculateSingleDayDaily,
		clearDailyVisualization,
		startFacadeAnimation,
		stopFacadeAnimation,
		isAnimatingFacade = false,
		calculateSingleDayFacade,
		clearFacadeVisualization,
		disabled = false,
		calculatingInProgress = false,
	} = props;

	// Determine which animation functions to use based on type
	const startAnimation = animationType === 'daily' ? startDailyAnimation : startFacadeAnimation;
	const stopAnimation = animationType === 'daily' ? stopDailyAnimation : stopFacadeAnimation;
	const isAnimating = animationType === 'daily' ? isAnimatingDaily : isAnimatingFacade;
	const calculateSingleDay =
		animationType === 'daily' ? calculateSingleDayDaily : calculateSingleDayFacade;

	const currentAnimationDay = useDailySunlightStore((state) =>
		animationType === 'daily' ? state.currentDailyAnimationDay : state.currentAnimationDay
	);
	const setCurrentAnimationDay = useDailySunlightStore((state) =>
		animationType === 'daily' ? state.setCurrentDailyAnimationDay : state.setCurrentAnimationDay
	);
	const startDate = useDailySunlightStore((state) => state.startDate);
	const endDate = useDailySunlightStore((state) => state.endDate);
	const useApproximation = useDailySunlightStore((state) => state.useApproximation);

	const getAnalysisDates = () => {
		if (!startDate || !endDate) return [];
		return buildDateSequence(startDate, endDate, useApproximation);
	};

	const getTotalDays = () => {
		return getAnalysisDates().length;
	};

	const getCurrentDate = () => {
		const analysisDates = getAnalysisDates();
		if (currentAnimationDay >= analysisDates.length) return '';
		const dateStr = analysisDates[currentAnimationDay];
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	};

	const handlePrevDay = async () => {
		if (currentAnimationDay > 0 && calculateSingleDay) {
			const newDay = currentAnimationDay - 1;
			setCurrentAnimationDay(newDay);
			await calculateSingleDay(newDay);
		}
	};

	const handleNextDay = async () => {
		const totalDays = getTotalDays();
		if (currentAnimationDay < totalDays - 1 && calculateSingleDay) {
			const newDay = currentAnimationDay + 1;
			setCurrentAnimationDay(newDay);
			await calculateSingleDay(newDay);
		}
	};

	const handleDateChange = async (event: { detail: { value: string } }) => {
		const dayIndex = parseInt(event.detail.value, 10);
		if (!isNaN(dayIndex) && calculateSingleDay) {
			setCurrentAnimationDay(dayIndex);
			await calculateSingleDay(dayIndex);
		}
	};

	useEffect(() => {
		const handleAnimationTypeChange = (newType: AnimationType) => {
			// Clear the other animation type's visualization when switching
			if (newType === 'daily' && clearFacadeVisualization) {
				clearFacadeVisualization();
			} else if (newType === 'facade' && clearDailyVisualization) {
				clearDailyVisualization();
			}
		};

		handleAnimationTypeChange(animationType);
	}, [animationType, clearDailyVisualization, clearFacadeVisualization]);

	// Don't render if no animation functions are provided
	if (!startAnimation || !stopAnimation || !calculateSingleDay) {
		return null;
	}

	return (
		<>
			<div className={styles.animationControls}>
				<WeaveButton
					onClick={isAnimating ? stopAnimation : startAnimation}
					variant={isAnimating ? 'outlined' : 'solid'}
				>
					{isAnimating ? '⏸ Pause Animation' : '▶ Play Year Animation'}
				</WeaveButton>
				{!isAnimating && (
					<div className={styles.dayNavigationControls}>
						<WeaveIconButton
							onClick={handlePrevDay}
							disabled={currentAnimationDay === 0 || calculatingInProgress || disabled}
						>
							<Icon name="forma-icon-arrow-left" className={styles.icon} />
						</WeaveIconButton>
						<Select
							options={getAnalysisDates().map((dateStr, index) => {
								const date = new Date(dateStr);
								const formattedDate = date.toLocaleDateString('en-US', {
									month: 'short',
									day: 'numeric',
									year: 'numeric',
								});
								return {
									label: formattedDate,
									value: index,
								};
							})}
							value={currentAnimationDay}
							onChange={handleDateChange}
							disabled={calculatingInProgress || disabled}
						/>
						<WeaveIconButton
							onClick={handleNextDay}
							disabled={
								currentAnimationDay >= getTotalDays() - 1 ||
								calculatingInProgress ||
								disabled
							}
						>
							<Icon name="forma-icon-arrow-right" className={styles.icon} />
						</WeaveIconButton>
					</div>
				)}
				{isAnimating && (
					<div className={styles.animationStatus}>
						Day {currentAnimationDay + 1}/{getTotalDays()} ({getCurrentDate()})
					</div>
				)}
			</div>
		</>
	);
};
